"use client";

import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/new/carousel";
import { Card, CardContent } from "@/components/new/card";
import GlobalSearch from "@/modules/home/components/hero-section/components/global-search";
import HeroHeading from "@/modules/home/components/hero-section/components/heading";
import Image from "next/image";
import CountryStats from "./country-stats";
import { ArrowUpRightIcon } from "lucide-react";
import Link from "next/link";
import { useModal } from "@/providers/modal-provider";
import { Button } from "@/components/new/button";
import { useCountryContext } from "@/providers/country-provider";
import ProfilePicture from "@/components/common/profile-picture";
import useCommunityAndChannelParams from "@/hooks/useChannelAndCommunityParams";

type HeroSectionProps = {
	country?: string;
};

type Ambassador = {
	image: string;
	name: string;
	company: string;
	profileLink: string;
};

export const HeroSection = ({ country }: HeroSectionProps) => {
	const { openContactUsModal } = useModal();
	console.log("HeroSection: Client component using client hook");
	const { hero, ambassadors } = useCountryContext();
	const { communitySlug } = useCommunityAndChannelParams();

	const renderAmbassador = (ambassador: Ambassador) => (
		<div key={ambassador.name} className="flex flex-col items-center gap-4 text-center">
			<ProfilePicture
				profilePictureUrl={ambassador.image}
				alternateText={ambassador.name}
				isAmbassador={true}
				isVerified={true}
				isFounder={true}
				className={{
					container: "h-24 w-24 rounded-full lg:h-32 lg:w-32",
					image: "h-full w-full rounded-full",
					badgeImage: "w-16",
				}}
			/>
			<Link href={ambassador.profileLink} className="space-y-2">
				<p className="text-[17px] font-bold">{ambassador.name}</p>
				<p className="text-[13px] font-semibold text-opacity-80">{ambassador.company}</p>
			</Link>
		</div>
	);

	return (
		<>
			<div
				className="mx-auto flex min-h-max flex-col gap-10 rounded-3xl bg-cover bg-center bg-no-repeat px-6 py-12 text-center md:px-0 md:py-12"
				style={{ backgroundImage: `url(${hero.backgroundImage})` }}
			>
				<div className="space-y-[20px]">
					<div className="flex justify-center gap-10">
						<Image
							src={hero.flagImage}
							width={120}
							height={120}
							alt={hero.flagAlt}
							className="h-[80px] w-[80px] rounded-[10px] bg-gradient-to-br from-[#82B0FF] to-[#005EFF] p-4"
						/>
						<HeroHeading
							className="mx-0"
							text={{
								title: hero.title,
								screenReader: hero.screenReaderTitle,
							}}
						/>
					</div>
					<p className="mx-auto max-w-4xl text-xl font-bold text-[#1A2133]">{hero.description}</p>
				</div>

				<GlobalSearch
					country={country}
					classNames={{ container: "md:w-[770px] mx-auto max-w-4xl" }}
				/>

				<div className="flex flex-wrap items-center justify-center gap-8 text-left">
					<CountryStats />
				</div>
			</div>
			<div className="mb-20 flex items-center justify-center gap-10 bg-gradient-to-br from-[#005EFF66] to-[#FFFFFF] px-4 py-6 text-center">
				<div className="space-y-4 rounded-xl bg-white p-4 text-[#3E3E3E]">
					<div className="flex items-center gap-1">
						<span className="text-xl font-bold uppercase tracking-widest ">Ambassador</span>

						<ArrowUpRightIcon className="h-6 w-6" />
					</div>
					<div className="flex flex-wrap items-center justify-center gap-2">
						{ambassadors.length > 1 ? (
							<Carousel className="w-full md:w-3/4">
								<CarouselContent>
									{ambassadors.map((ambassador, index) => (
										<CarouselItem key={index}>
											<div className="p-2">
												<Card className="h-full">
													<CardContent className="flex flex-col items-center justify-center space-y-4 p-6">
														<ProfilePicture
															profilePictureUrl={ambassador.image}
															alternateText={ambassador.name}
															isAmbassador={true}
															isVerified={true}
															isFounder={true}
															className={{
																container: "h-20 w-20 rounded-full lg:h-24 lg:w-24",
																image: "h-full w-full rounded-full",
																badgeImage: "w-12",
															}}
														/>
														<div className="text-center">
															<Link href={ambassador.profileLink}>
																<p className="text-lg font-bold">{ambassador.name}</p>
															</Link>
															<p className="w-full break-words text-sm font-semibold text-opacity-80 md:w-auto md:max-w-[170px]">
																{ambassador.company}
															</p>
														</div>
													</CardContent>
												</Card>
											</div>
										</CarouselItem>
									))}
								</CarouselContent>
								<CarouselPrevious />
								<CarouselNext />
							</Carousel>
						) : (
							<div className="flex flex-col items-center gap-4 text-center">
								{ambassadors.map(renderAmbassador)}
							</div>
						)}
					</div>
				</div>
				<div className="text-[42px] font-extrabold">
					<p className="text-[#005EFF]">{hero.welcomeMessage}</p>
					<p className="text-[#1A2133]">How Can We Help?</p>
					<Button
						onClick={() => openContactUsModal()}
						className="rounded-[10px] border border-gray-300 bg-[#005EFF] px-4 py-3 font-inter text-[17px] text-white"
					>
						Contact Us
					</Button>
					{communitySlug === "proptech-india" && (
						<Link
							href="/services/oneNDF"
							className="block py-3 text-base font-medium text-[#005EFF] underline"
						>
							Unlock Construction Finance - Discover Our Developer Lending Platform
						</Link>
					)}
				</div>
			</div>
		</>
	);
};
